// Where the widget opens — pure, so the rules are testable without a browser
// (e2e/widget-landing-logic.spec.ts).
//
// A link can name a tab (`?tab=` / data-default-tab: a service slug or a
// pseudo-tab), an event (`?event=`), a package (`?combo=`) or one of a
// service's options (`?option=` / data-option: a ride, a room, …). Newsletter
// cards (packages/email, vertu-blocks.shared.ts) link to all of them, so each
// must land where it says — and never on a tab the widget is not showing.

export const EVENTS_TAB = "__events__";
export const PACKAGES_TAB = "__packages__";
export const FUNCTIONS_TAB = "__functions__";
export const ENQUIRIES_TAB = "__enquiries__";
export const GIFT_CARDS_TAB = "__gift_cards__";

type LandingService = { id: string; slug: string };

/**
 * Resolve a landing-tab key (a service id/slug, or a pseudo-tab) to the
 * activeId the shell should show — falling back to the default service, then
 * null. Shared by the initial landing and the post-booking success redirect.
 *
 * Every pseudo-tab the TabBar can render must be landable. __functions__ and
 * __enquiries__ were missing once, so "&tab=__functions__" silently fell
 * through to the default service; __gift_cards__ was missing until the
 * newsletter's gift-card block needed a link that opens it. Each is gated on
 * the tab actually being shown, so a link can never land on a tab that isn't
 * there. (__packages__ is not gated here: its tab appears once the packages
 * load, and the shell's landing safety net falls back if none do.)
 */
export function resolveLandingTab(
  key: string | null,
  showEventsTab: boolean,
  services: LandingService[],
  defaultService: LandingService | null,
  showFunctionsTab = false,
  showEnquiriesTab = false,
  showGiftCardsTab = false,
): string | null {
  if (key === EVENTS_TAB && showEventsTab) return EVENTS_TAB;
  if (key === PACKAGES_TAB) return PACKAGES_TAB;
  if (key === FUNCTIONS_TAB && showFunctionsTab) return FUNCTIONS_TAB;
  if (key === ENQUIRIES_TAB && showEnquiriesTab) return ENQUIRIES_TAB;
  if (key === GIFT_CARDS_TAB && showGiftCardsTab) return GIFT_CARDS_TAB;
  if (key) {
    const svc = services.find((s) => s.id === key || s.slug === key);
    if (svc) return svc.id;
  }
  return defaultService?.id ?? null;
}

/**
 * The tab a fresh mount opens on. The most specific instruction wins:
 *   1. an event deep link that matched an event in this widget's list;
 *   2. a package deep link (`?combo=<slug>`) — the Packages tab, where the
 *      package's own detail opens once the packages have loaded
 *      (`comboDeepLinkTarget`); if no such package is bookable here the
 *      guest still lands on the packages list;
 *   3. the tab named on the link or embed, else the widget's configured
 *      default (`resolveLandingTab`).
 */
export function resolveInitialLanding(input: {
  eventMatched: boolean;
  comboSlug?: string | null;
  tabKey: string | null;
  showEventsTab: boolean;
  services: LandingService[];
  defaultService: LandingService | null;
  showFunctionsTab?: boolean;
  showEnquiriesTab?: boolean;
  showGiftCardsTab?: boolean;
}): string | null {
  if (input.eventMatched) return EVENTS_TAB;
  if ((input.comboSlug ?? "").trim()) return PACKAGES_TAB;
  return resolveLandingTab(
    input.tabKey,
    input.showEventsTab,
    input.services,
    input.defaultService,
    input.showFunctionsTab ?? false,
    input.showEnquiriesTab ?? false,
    input.showGiftCardsTab ?? false,
  );
}

/**
 * The package a `?combo=<slug>` link opens: its id among the packages this
 * widget can book (the shell's brand- and service-filtered list), or null —
 * unknown, unpublished, or not bookable here — in which case the guest stays
 * on the packages list rather than being bounced somewhere unrelated.
 */
export function comboDeepLinkTarget(
  slug: string | null | undefined,
  combos: ReadonlyArray<{ id: string; slug: string }>,
): string | null {
  const s = (slug ?? "").trim();
  if (!s) return null;
  return combos.find((c) => c.slug === s)?.id ?? null;
}

export type OptionLinkTarget = { serviceId: string; serviceSlug: string; unitId: string };

/**
 * The option a `?option=<slug>` link (or data-option) opens with chosen: its
 * service and its id. A link that also names a service tab looks only in
 * that service; one that names a pseudo-tab (Events, Enquire, …) means that
 * tab, so no option is picked; otherwise the first shown service with an
 * option of that slug. Null when nothing matches: the guest lands as the
 * link would have without it, nothing chosen.
 */
export function optionDeepLinkTarget(
  optionSlug: string | null | undefined,
  tabKey: string | null | undefined,
  services: ReadonlyArray<{ id: string; slug: string; units: ReadonlyArray<{ id: string; slug: string }> }>,
): OptionLinkTarget | null {
  const slug = (optionSlug ?? "").trim();
  if (!slug) return null;
  const tab = (tabKey ?? "").trim();
  if (tab.startsWith("__")) return null;
  const named = tab ? services.find((s) => s.id === tab || s.slug === tab) : undefined;
  for (const s of named ? [named] : services) {
    const unit = s.units.find((u) => u.slug === slug);
    if (unit) return { serviceId: s.id, serviceSlug: s.slug, unitId: unit.id };
  }
  return null;
}

/**
 * The shell's landing-tab safety net: whether `activeId` stays as it is. A tab
 * the widget shows always stays. So does a pseudo-tab whose TAB is hidden but
 * whose panel has something to show, because the widget navigates there on
 * purpose: a service's "What's on" card opens its event in the Events panel
 * with show_events_tab off, and package links open the Packages panel with
 * its tab hidden. Anything else (a stale default such as a disabled service,
 * or a pseudo-tab with nothing to show) goes back to the first tab.
 */
export function keepsActiveTab(
  activeId: string,
  shownTabIds: ReadonlyArray<string>,
  content: { events: number; combos: number },
): boolean {
  if (shownTabIds.includes(activeId)) return true;
  if (activeId === EVENTS_TAB) return content.events > 0;
  if (activeId === PACKAGES_TAB) return content.combos > 0;
  return false;
}

/**
 * Whether the tab bar is worth showing. A widget with one tab (a single
 * service and no Events / Packages / Functions / Gift cards / Enquire) has
 * nowhere else to go, and the panel's own header already names the service —
 * so the bar is hidden. It stays when the guest is on a panel that is not that
 * one tab (a hidden pseudo-tab reached from a "What's on" card or a package
 * link): there the tab is the way back. A null activeId is the first tab.
 */
export function showsTabBar(
  shownTabIds: ReadonlyArray<string>,
  activeId: string | null,
): boolean {
  if (shownTabIds.length !== 1) return shownTabIds.length > 1;
  return activeId !== null && activeId !== shownTabIds[0];
}
